import { useState } from "react";
import { supabase } from "../supabaseClient";

export function useLearningProgress(session) {
  const [topicProgress, setTopicProgress] = useState([]);
  const [loadingTopicProgress, setLoadingTopicProgress] = useState(false);

  const fetchTopicProgress = async (topicIds = []) => {
    if (!session?.user?.id) return [];
    if (!topicIds.length) {
      setTopicProgress([]);
      return [];
    }

    setLoadingTopicProgress(true);

    const { data, error } = await supabase
      .from("learning_user_topic_progress")
      .select("*")
      .eq("user_id", session.user.id)
      .in("topic_id", topicIds);

    if (error) {
      console.error("FETCH TOPIC PROGRESS ERROR:", error);
      setLoadingTopicProgress(false);
      return [];
    }

    setTopicProgress(data || []);
    setLoadingTopicProgress(false);

    return data || [];
  };

  const markTopicReviewed = async (topicId) => {
    if (!session?.user?.id || !topicId) return false;

    const now = new Date().toISOString();

    const { error } = await supabase
      .from("learning_user_topic_progress")
      .upsert(
        {
          user_id: session.user.id,
          topic_id: topicId,
          review_status: "reviewed",
          reviewed_at: now,
          last_viewed_at: now,
          updated_at: now,
        },
        {
          onConflict: "user_id,topic_id",
        }
      );

    if (error) {
      console.error("MARK TOPIC REVIEWED ERROR:", error);
      alert(error.message);
      return false;
    }

    setTopicProgress((prev) => {
      const exists = prev.some((p) => p.topic_id === topicId);

      if (exists) {
        return prev.map((p) =>
          p.topic_id === topicId
            ? {
                ...p,
                review_status: "reviewed",
                reviewed_at: now,
                last_viewed_at: now,
                updated_at: now,
              }
            : p
        );
      }

      return [
        ...prev,
        {
          user_id: session.user.id,
          topic_id: topicId,
          review_status: "reviewed",
          reviewed_at: now,
          last_viewed_at: now,
          updated_at: now,
        },
      ];
    });

    return true;
  };

  const undoTopicReviewed = async (topicId) => {
    if (!session?.user?.id || !topicId) return false;

    const now = new Date().toISOString();

    const { error } = await supabase
      .from("learning_user_topic_progress")
      .upsert(
        {
          user_id: session.user.id,
          topic_id: topicId,
          review_status: "not_started",
          reviewed_at: null,
          last_viewed_at: now,
          updated_at: now,
        },
        {
          onConflict: "user_id,topic_id",
        }
      );

    if (error) {
      console.error("UNDO TOPIC REVIEW ERROR:", error);
      alert(error.message);
      return false;
    }

    setTopicProgress((prev) =>
      prev.map((p) =>
        p.topic_id === topicId
          ? {
              ...p,
              review_status: "not_started",
              reviewed_at: null,
              updated_at: now,
            }
          : p
      )
    );

    return true;
  };

  const getTopicReviewStatus = (topicId) => {
    return (
      topicProgress.find((p) => p.topic_id === topicId)?.review_status ||
      "not_started"
    );
  };

  const syncLearningProgress = async ({
    mainTopicId,
    childTopicIds,
  }) => {
    if (!session?.user?.id) return false;
    if (!mainTopicId) return false;

    const ids = childTopicIds || [];

    if (ids.length === 0) {
        return false;
    }

    // 1. 读取当前学生这些 child topics 的 progress
    const { data: progressRows, error: progressError } = await supabase
        .from("learning_user_topic_progress")
        .select("topic_id, review_status")
        .eq("user_id", session.user.id)
        .in("topic_id", ids);

    if (progressError) {
        console.error(
        "FETCH CHILD TOPIC PROGRESS ERROR:",
        progressError
        );
        return false;
    }

    // 2. 计算 reviewed 数量
    const reviewedTopicIds = new Set(
        (progressRows || [])
        .filter((row) => row.review_status === "reviewed")
        .map((row) => row.topic_id)
    );

    const reviewedCount = ids.filter((id) =>
        reviewedTopicIds.has(id)
    ).length;

    const completionPct = Math.round(
        (reviewedCount / ids.length) * 100
    );

    const now = new Date().toISOString();

    // 3. 只更新 completion，不动 mastery/exercise 字段
    const { error: upsertError } = await supabase
        .from("learning_user_progress")
        .upsert(
        {
            user_id: session.user.id,
            topic_id: mainTopicId,
            completion_pct: completionPct,
            last_studied_at: now,
            updated_at: now,
        },
        {
            onConflict: "user_id,topic_id",
        }
        );

    if (upsertError) {
        console.error(
        "SYNC LEARNING PROGRESS ERROR:",
        upsertError
        );
        return false;
    }

    return true;
    };

  return {
    topicProgress,
    loadingTopicProgress,
    fetchTopicProgress,
    markTopicReviewed,
    undoTopicReviewed,
    getTopicReviewStatus,
    syncLearningProgress,
  };
}